import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DeviceMobile,
  DotsThree,
  FolderSimplePlus,
  LinkBreak,
  Plus,
  Power,
  QrCode,
  ArrowClockwise,
  ShieldCheck,
  WifiHigh,
} from "@phosphor-icons/react";
import QRCode from "qrcode";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../app/auth";
import { MetricStrip, Modal, SearchInput, StatusTag } from "../../components/Primitives";
import { QueryErrorState } from "../../components/States";
import { useToast } from "../../components/Toast";
import { formatRelativeTime } from "../../lib/format";
import { useApiQuery } from "../../lib/query";
import type { Device, DeviceGroup } from "../../lib/types";
import { DeviceDetail, TaskDispatchModal } from "../overview/OverviewPage";

interface BindingResponse {
  bindingId: string;
  qrPayload: Record<string, unknown>;
  reused: boolean;
}

function BindingModal({
  payload,
  onClose,
  onRefresh,
  refreshing,
  refreshError,
}: {
  payload: BindingResponse;
  onClose(): void;
  onRefresh(): void;
  refreshing: boolean;
  refreshError: boolean;
}) {
  const [image, setImage] = useState("");
  useEffect(() => {
    let active = true;
    void QRCode.toDataURL(JSON.stringify(payload.qrPayload), {
      width: 260,
      margin: 2,
      color: { dark: "#17223a", light: "#ffffff" },
      errorCorrectionLevel: "M",
    }).then((url) => {
      if (active) setImage(url);
    });
    return () => {
      active = false;
    };
  }, [payload]);

  return (
    <Modal
      title="绑定新设备"
      description="使用 XMirror 工作手机扫描二维码，二维码 5 分钟内有效。"
      onClose={onClose}
      footer={
        <>
          <button className="secondary-button" disabled={refreshing} onClick={onRefresh}>
            <ArrowClockwise className={refreshing ? "spin" : ""} size={17} />
            {refreshing ? "正在刷新…" : "刷新二维码"}
          </button>
          <button className="primary-button" onClick={onClose}>
            完成
          </button>
        </>
      }
    >
      <div className="binding-qr">
        <div className="qr-frame">
          {image ? <img src={image} alt="设备绑定二维码" /> : <span>正在生成二维码…</span>}
        </div>
        <strong>等待设备扫码</strong>
        <small>
          {payload.reused ? "继续使用当前有效二维码" : "已生成新的二维码"}
          {` · 绑定编号 ${payload.bindingId}`}
        </small>
        <p>请勿通过截图分享二维码；绑定成功后，本页设备列表会自动刷新。</p>
        {refreshError && <p className="inline-error">二维码刷新失败，请稍后重试。</p>}
      </div>
    </Modal>
  );
}

function DeviceOperationModal({
  device,
  action,
  onClose,
}: {
  device: Device;
  action: "enable" | "disable" | "unbind";
  onClose(): void;
}) {
  const { request } = useAuth();
  const notify = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const labels = {
    enable: { title: "启用设备", button: "确认启用", message: "设备已启用" },
    disable: { title: "禁用设备", button: "确认禁用", message: "设备已禁用" },
    unbind: { title: "解绑设备", button: "确认解绑", message: "设备已解绑" },
  }[action];
  const mutation = useMutation({
    mutationFn: () =>
      request<Device>(`/api/devices/${device.id}/${action}`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
      notify(labels.message, device.info?.model || `设备 #${device.id}`);
      onClose();
    },
  });

  return (
    <Modal
      title={labels.title}
      description={`${device.info?.model || `设备 #${device.id}`} · ${device.info?.deviceSn || device.id}`}
      onClose={onClose}
      footer={
        <>
          <button className="secondary-button compact" onClick={onClose}>取消</button>
          <button
            className={`primary-button ${action === "unbind" ? "danger-button" : ""}`}
            disabled={mutation.isPending || !reason.trim()}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "正在处理…" : labels.button}
          </button>
        </>
      }
    >
      <div className="form-stack">
        <p className="operation-note">
          {action === "unbind"
            ? "解绑会撤销设备凭证并解除员工账号关系，设备需重新扫码后才能接入。"
            : action === "disable"
              ? "禁用后设备将停止接收新任务，历史数据和审计记录不会删除。"
              : "启用后设备可恢复连接并继续接收任务。"}
        </p>
        <label>
          <span>操作原因</span>
          <textarea
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="必填，将写入审计日志"
          />
        </label>
        {mutation.isError && <p className="form-error">操作失败，请确认设备状态和权限。</p>}
      </div>
    </Modal>
  );
}

function DeviceGroupModal({
  devices,
  groups,
  onClose,
}: {
  devices: Device[];
  groups: DeviceGroup[];
  onClose(): void;
}) {
  const { request } = useAuth();
  const notify = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"existing" | "new">(() =>
    groups.length ? "existing" : "new",
  );
  const [groupId, setGroupId] = useState(groups[0]?.id || "");
  const [newName, setNewName] = useState("");
  const canSubmit =
    mode === "existing" ? Boolean(groupId) : Boolean(newName.trim());
  const mutation = useMutation({
    mutationFn: async () => {
      let targetId = groupId;
      if (mode === "new") {
        const created = await request<DeviceGroup>("/api/device-groups", {
          method: "POST",
          body: JSON.stringify({ name: newName.trim() }),
        });
        targetId = created.id;
      }
      if (!targetId) throw new Error("device_group_required");
      return request(`/api/device-groups/${targetId}/members`, {
        method: "POST",
        body: JSON.stringify({ deviceIds: devices.map((device) => device.id) }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["device-groups"] });
      notify("设备已加入分组", `${devices.length} 台设备`);
      onClose();
    },
  });
  return (
    <Modal title="加入设备分组" description={devices.length === 1 ? devices[0].info?.model || `设备 #${devices[0].id}` : `批量处理 ${devices.length} 台设备`} onClose={onClose} footer={
      <>
        <button className="secondary-button compact" onClick={onClose}>取消</button>
        <button className="primary-button" disabled={mutation.isPending || !canSubmit} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "正在保存…" : "确认加入"}
        </button>
      </>
    }>
      <div className="form-stack device-group-form">
        <div className="segmented device-group-mode" role="tablist" aria-label="选择分组加入方式">
          <button type="button" role="tab" aria-selected={mode === "existing"} className={mode === "existing" ? "active" : ""} disabled={!groups.length} onClick={() => setMode("existing")}>
            <FolderSimplePlus size={15} />选择已有分组
          </button>
          <button type="button" role="tab" aria-selected={mode === "new"} className={mode === "new" ? "active" : ""} onClick={() => setMode("new")}>
            <Plus size={15} />创建新分组
          </button>
        </div>
        {mode === "existing" ? (
          <div role="tabpanel" className="device-group-field">
            <label>
              <span>选择分组</span>
              <select value={groupId} onChange={(event) => setGroupId(event.target.value)}>
                <option value="">请选择分组</option>
                {groups.map((group) => <option key={group.id} value={group.id}>{group.name}（{group.memberCount} 台设备）</option>)}
              </select>
            </label>
            <p>将 {devices.length} 台设备加入所选分组，不影响设备现有绑定关系。</p>
          </div>
        ) : (
          <div role="tabpanel" className="device-group-field">
            <label>
              <span>新分组名称</span>
              <input type="text" value={newName} onChange={(event) => setNewName(event.target.value)} placeholder="例如：华东门店" autoFocus />
            </label>
            <p>保存时创建分组，并自动加入当前 {devices.length} 台设备。</p>
          </div>
        )}
        {mutation.isError && <p className="form-error">分组保存失败，请检查名称是否重复。</p>}
      </div>
    </Modal>
  );
}

export function DevicesPage() {
  const polling = Number(import.meta.env.VITE_QUERY_POLL_INTERVAL_MS || 10000);
  const { request, hasPermission } = useAuth();
  const [searchParams] = useSearchParams();
  const linkedDeviceId = searchParams.get("deviceId");
  const devicesQuery = useApiQuery<Device[]>(["devices"], "/api/devices?limit=200", {
    refetchInterval: polling,
  });
  const groupsQuery = useApiQuery<DeviceGroup[]>(["device-groups"], "/api/device-groups");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [groupId, setGroupId] = useState("all");
  const [detailId, setDetailId] = useState<string | null>(() => linkedDeviceId);
  const [dispatchDevice, setDispatchDevice] = useState<Device | null>(null);
  const [operation, setOperation] = useState<{
    device: Device;
    action: "enable" | "disable" | "unbind";
  } | null>(null);
  const [binding, setBinding] = useState<BindingResponse | null>(null);
  const [groupDevice, setGroupDevice] = useState<Device | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkGroupOpen, setBulkGroupOpen] = useState(false);
  const bindingMutation = useMutation({
    mutationFn: (forceRefresh: boolean) =>
      request<BindingResponse>("/api/bindings/qrcode", {
        method: "POST",
        body: JSON.stringify({ forceRefresh }),
      }),
    onSuccess: setBinding,
  });
  const devices = devicesQuery.data || [];
  const groups = groupsQuery.data || [];
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return devices.filter((device) => {
      const matchesStatus = status === "all" || device.status === status;
      const matchesGroup =
        groupId === "all" ||
        groups.find((group) => group.id === groupId)?.members.some((member) => member.deviceId === device.id);
      const matchesQuery =
        !normalized ||
        [device.id, device.info?.model, device.info?.deviceSn, device.account?.username]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalized);
      return matchesStatus && matchesGroup && matchesQuery;
    });
  }, [devices, groupId, groups, query, status]);
  const online = devices.filter((device) => device.status === "online").length;
  const disabled = devices.filter((device) => device.status === "disabled").length;
  const expiring = devices.filter(
    (device) => device.tokenExpired || device.tokenExpiringSoon,
  ).length;

  if (devicesQuery.isError || groupsQuery.isError) {
    return <QueryErrorState onRetry={() => { void devicesQuery.refetch(); void groupsQuery.refetch(); }} />;
  }

  return (
    <div className="module-page">
      <MetricStrip
        metrics={[
          { label: "设备总数", value: devices.length, note: "当前租户全部设备" },
          { label: "在线设备", value: online, note: "网关连接正常", tone: "metric-green" },
          { label: "已禁用", value: disabled, note: "停止接收新任务" },
          { label: "凭证需处理", value: expiring, note: "过期或即将过期", tone: "metric-red" },
        ]}
      />
      <section className="table-panel">
        <header className="module-toolbar">
          <div className="toolbar-left">
            <SearchInput value={query} onChange={setQuery} placeholder="搜索设备、序列号或账号" />
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="all">全部状态</option>
              <option value="online">在线</option>
              <option value="offline">离线</option>
              <option value="disabled">已禁用</option>
              <option value="unbound">未绑定</option>
            </select>
            <select value={groupId} onChange={(event) => setGroupId(event.target.value)}>
              <option value="all">全部分组</option>
              {groups.map((group) => <option key={group.id} value={group.id}>{group.name}（{group.memberCount}）</option>)}
            </select>
          </div>
          {hasPermission("bindings:create") && (
            <button
              className="primary-button"
              disabled={bindingMutation.isPending}
              onClick={() => bindingMutation.mutate(false)}
            >
              <Plus size={17} />
              {bindingMutation.isPending ? "正在生成…" : "绑定新设备"}
            </button>
          )}
        </header>
        {selectedIds.length > 0 && <div className="bulk-toolbar"><span><strong>{selectedIds.length}</strong> 台设备已选择</span><button className="secondary-button compact" onClick={() => setBulkGroupOpen(true)}><FolderSimplePlus size={16} />批量加入分组</button><button className="text-button" onClick={() => setSelectedIds([])}>取消选择</button></div>}
        {bindingMutation.isError && (
          <div className="inline-error">绑定二维码生成失败，请检查网络与权限。</div>
        )}
        <div className="data-table-wrap">
          <table className="data-table device-management-table">
            <thead>
              <tr>
                <th className="selection-cell"><input aria-label="选择当前筛选下的全部设备" type="checkbox" checked={filtered.length > 0 && filtered.every((device) => selectedIds.includes(device.id))} onChange={(event) => setSelectedIds(event.target.checked ? [...new Set([...selectedIds, ...filtered.map((device) => device.id)])] : selectedIds.filter((id) => !filtered.some((device) => device.id === id)))} /></th>
                <th>设备</th>
                <th>绑定账号</th>
                <th>状态</th>
                <th>最近心跳</th>
                <th>客户端</th>
                <th>凭证</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((device) => (
                <tr key={device.id} tabIndex={0} onClick={() => setDetailId(device.id)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") setDetailId(device.id); }}>
                  <td className="selection-cell" onClick={(event) => event.stopPropagation()}><input aria-label={`选择${device.info?.model || `设备 #${device.id}`}`} type="checkbox" checked={selectedIds.includes(device.id)} onChange={() => setSelectedIds((current) => current.includes(device.id) ? current.filter((id) => id !== device.id) : [...current, device.id])} /></td>
                  <td>
                    <span className="device-cell">
                      <DeviceMobile size={20} />
                      <span><strong>{device.info?.model || `设备 #${device.id}`}</strong><small>{device.info?.deviceSn || device.id}</small></span>
                    </span>
                  </td>
                  <td>{device.account?.username || "未绑定"}</td>
                  <td><StatusTag value={device.status} /></td>
                  <td>{formatRelativeTime(device.lastSeenAt)}</td>
                  <td>{device.info?.appVersion || "—"}</td>
                  <td>
                    {device.tokenExpired ? (
                      <StatusTag value="已过期" tone="red" />
                    ) : device.tokenExpiringSoon ? (
                      <StatusTag value="即将过期" tone="amber" />
                    ) : (
                      <span className="text-green"><ShieldCheck size={16} /> 正常</span>
                    )}
                  </td>
                  <td onClick={(event) => event.stopPropagation()}>
                    <div className="row-actions">
                      {hasPermission("tasks:create") && (
                        <button title="下发任务" onClick={() => setDispatchDevice(device)}>
                          <WifiHigh size={16} />
                        </button>
                      )}
                      {hasPermission("devices:manage") && (
                        <>
                          <button title="加入分组" onClick={() => setGroupDevice(device)}>
                            <FolderSimplePlus size={16} />
                          </button>
                          <button
                            title={device.status === "disabled" ? "启用" : "禁用"}
                            onClick={() =>
                              setOperation({
                                device,
                                action: device.status === "disabled" ? "enable" : "disable",
                              })
                            }
                          >
                            <Power size={16} />
                          </button>
                          <button title="解绑" onClick={() => setOperation({ device, action: "unbind" })}>
                            <LinkBreak size={16} />
                          </button>
                        </>
                      )}
                      {!hasPermission("tasks:create") && !hasPermission("devices:manage") && (
                        <DotsThree size={18} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!devicesQuery.isPending && !filtered.length && (
            <div className="table-empty">没有符合当前筛选条件的设备。</div>
          )}
          {devicesQuery.isPending && <div className="table-empty">正在加载设备…</div>}
        </div>
      </section>
      {binding && (
        <BindingModal
          payload={binding}
          onClose={() => setBinding(null)}
          onRefresh={() => bindingMutation.mutate(true)}
          refreshing={bindingMutation.isPending}
          refreshError={bindingMutation.isError}
        />
      )}
      {detailId && (
        <DeviceDetail
          deviceId={detailId}
          onClose={() => setDetailId(null)}
          onDispatch={(device) => {
            setDetailId(null);
            setDispatchDevice(device);
          }}
        />
      )}
      {dispatchDevice && (
        <TaskDispatchModal device={dispatchDevice} onClose={() => setDispatchDevice(null)} />
      )}
      {operation && (
        <DeviceOperationModal
          device={operation.device}
          action={operation.action}
          onClose={() => setOperation(null)}
        />
      )}
      {groupDevice && (
        <DeviceGroupModal devices={[groupDevice]} groups={groups} onClose={() => setGroupDevice(null)} />
      )}
      {bulkGroupOpen && <DeviceGroupModal devices={devices.filter((device) => selectedIds.includes(device.id))} groups={groups} onClose={() => setBulkGroupOpen(false)} />}
    </div>
  );
}
